<template>
  <v-dialog v-model="templateDialog" fullscreen transition="dialog-bottom-transition" :overlay="false">
    <v-card class="elevation-1 template-details">
      <v-layout column justify-space-between>
        <v-layout column>
          <!-- Modal header -->
          <v-toolbar color="secondary white--text">
            <v-toolbar-title class="subheading ttu">
              <span v-if="isEditMode"> {{ messages.label.EDIT_TEMPLATE }}</span>
              <span v-else>{{ messages.label.CREATE_TEMPLATE }}</span>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
              <v-btn @click.stop="hide" flat>
                <v-icon class="white--text">close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
          <!-- Main Content -->
          <v-card-text class="pl-3">
            <v-layout wrap>
              <!-- Success messages -->
              <v-flex xs12 v-if="templateSuccess">
                <v-card>
                  <v-card-text>
                    <v-alert color="success" icon="check_circle" value="true">
                      <span v-html="templateResponseMsg"></span>
                    </v-alert>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn color="primary" @click="hide">
                      {{ messages.label.DONE }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-flex>
              <!-- Tabs Items & content -->
              <v-flex xs12 v-else>
                <v-progress-linear :indeterminate="true" v-if="loadingTemplateDetails"></v-progress-linear>
                <!-- Template text error messages -->
                <v-alert color="error" icon="warning" v-model="errorAlert" dismissible>
                  <span v-html="templateTextError"></span>
                </v-alert>
                <!-- Failure messages -->
                <v-alert color="error" icon="warning" v-model="failureAlert" dismissible>
                  <span v-html="templateResponseMsg"></span>
                </v-alert>
                <!-- Tabs Items -->
                <v-tabs v-model="activeTab" v-if="templateDialog" slider-color="primary">
                  <v-tab v-for="item in createTemplateTabItems" :key="item">
                    {{ item }}
                  </v-tab>
                </v-tabs>
                <!-- Tabs Content -->
                <v-tabs-items v-model="activeTab">
                  <v-tab-item>
                    <v-flex xs6 class="mt-4 ml-2">
                      <v-form>
                        <v-flex xs11>
                          <v-combobox v-model="selectedProviderGroup" :disabled="isEditMode"
                            :label="messages.label.PROVIDER_GROUP" v-validate="'required'"
                            :loading="loadingProviderGroups" :error-messages="errors.collect('providerGroup')"
                            data-vv-name="providerGroup" :items="providerGroups" item-text="title" item-value="title"
                            return-object required>
                            <template v-slot:no-data>
                              <v-list-tile>
                                <v-list-tile-title>
                                  No Provider groups in
                                  <strong>{{ mpxParentAccount.title }}</strong>
                                </v-list-tile-title>
                              </v-list-tile>
                            </template>
                          </v-combobox>
                        </v-flex>
                        <v-flex xs11>
                          <v-text-field v-model="template.title" :clearable="!isEditMode" :disabled="isEditMode"
                            v-validate="'required|max:100|templateTitle'" :error-messages="errors.collect('title')"
                            data-vv-name="title" data-vv-delay="500" :counter="100" :label="messages.label.TITLE"
                            :loading="validatingTitle" required>
                          </v-text-field>
                        </v-flex>
                        <v-flex xs11>
                          <v-text-field v-model="template.templateGroupId" clearable v-validate="'max:50'"
                            :error-messages="errors.collect('templateGroupId')" data-vv-name="templateGroupId"
                            :counter="50" :label="messages.label.TEMPLATE_GROUP_ID">
                          </v-text-field>
                        </v-flex>
                        <v-flex xs11>
                          <v-text-field v-model="template.subDirectory" clearable
                            v-validate="{ max: 60, regex: /^[0-9a-zA-Z\s]*$/ }"
                            :error-messages="errors.collect('subDirectory')" data-vv-name="subDirectory" :counter="60"
                            :label="messages.label.SUB_DIRECTORY">
                          </v-text-field>
                        </v-flex>
                        <v-flex xs11>
                          <v-textarea v-model="template.description" clearable v-validate="'max:100'"
                            :error-messages="errors.collect('description')" data-vv-name="description" :counter="100"
                            :label="messages.label.DESCRIPTION" required>
                          </v-textarea>
                        </v-flex>
                        <v-flex xs11 class="add_margin">
                          <span class="subheading style_color">Enabled</span>
                          <v-switch v-model="enabledSwitch" color="primary" class="mt-0"
                            :label="`${enabledSwitch.toString()}`"></v-switch>
                        </v-flex>
                      </v-form>
                    </v-flex>
                  </v-tab-item>
                  <v-tab-item>
                    <v-card :height="editorHeight" fill-height class="elevation-0 mt-3">
                      <v-card-title>
                        <span class="body-2 grey--text text--darken-1">
                          {{ messages.label.TEMPLATE_TEXT }}</span>
                      </v-card-title>
                      <v-card-text class="ml-0">
                        <div ref="editorEditable" class="vop--ace-editor"></div>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-layout>
        <v-card-actions class="grey lighten-4 py-2" v-if="!templateSuccess">
          <v-btn color="grey lighten-1 white--text" @click="hide()">
            {{ messages.label.CANCEL }}
          </v-btn>
          <v-btn color="primary" :disabled="editingTemplate" :loading="editingTemplate" v-if="isEditMode"
            @click="validate()">
            {{ messages.label.SAVE }}
          </v-btn>
          <v-btn color="primary" :disabled="creatingTemplate" :loading="creatingTemplate" @click="validate()" v-else>
            {{ messages.label.CREATE }}
          </v-btn>
        </v-card-actions>
      </v-layout>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import { Validator } from "vee-validate"
import ace from "brace"
import "brace/mode/velocity"
import "brace/theme/eclipse"
import "brace/ext/searchbox"
import ProvidersService from "@/Providers/ProvidersService"
import * as providersDataService from "@/Providers/ProvidersDataService"
import messages from "@/Providers/messages.json"
import { GET_PROVIDER_GROUPS } from "@/store/constants/action-types"
import { VOD_SYCN_TEMPLATE_DATA_SERVICE_URL } from "@/store/constants/getter-types"

export default {
  name: "TemplatesManagement",
  components: {},
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return {
      templateDialog: false,
      createTemplateTabItems: ["INFO", "TEMPLATE"],
      activeTab: null,
      template: {},
      enabledSwitch: true,
      mpxToken: "",
      providerGroupsList: [],
      selectedProviderGroup: {},
      creatingTemplate: false,
      editingTemplate: false,
      templateTextError: "",
      errorAlert: false,
      failureAlert: false,
      templateSuccess: false,
      templateResponseMsg: "",
      validatingTitle: false,
      isEditMode: false,
      loadingTemplateDetails: false,
      loadingProviderGroups: false,
      createdOrUpdated: false,
      dictionary: this.getValidationDictionary()
    }
  },
  computed: {
    ...mapState(["providerGroups", "mpxParentAccount"]),
    ...mapGetters([VOD_SYCN_TEMPLATE_DATA_SERVICE_URL]),
    editorHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "65vh"
        case "sm":
          return "65vh"
        case "md":
          return "75vh"
        case "lg":
          return "75vh"
        case "xl":
          return "80vh"
      }
    }
  },
  methods: {
    ...mapActions([GET_PROVIDER_GROUPS]),

    /**
     * hide - hide manage template modal
     */
    hide() {
      if (this.createdOrUpdated) {
        ProvidersService.$emit("event:refreshTemplateList")
      }
      this.resetTemplateData()
      this.templateDialog = false
    },

    /**
     * show - Show Create and Edit Template Modal
     * @param {object} providerGroup
     * @param {object} template
     */
    show(providerGroup, template) {
      this.resetTemplateData()
      // Wait before updating form
      this.$nextTick(() => {
        this.$validator.reset()
        if (!this.$lodash.isUndefined(template)) {
          this.isEditMode = true
          this.loadTemplateDetails(template.id)
        } else {
          this.isEditMode = false
          this.templateDialog = true
          this.openXMLEditor("")
        }
        this.selectedProviderGroup = this.providerGroups.find(
          item => item.id === providerGroup.id
        )
      })
    },

    /**
     * loadTemplateDetails - Load selected template details
     * @param {string} templateId
     * @returns {Promise}
     */
    loadTemplateDetails(templateId) {
      this.loadingTemplateDetails = true
      return this.getSelectedTemplateDetails(templateId)
        .then(templateDetails => {
          this.template = this.$lodash.cloneDeep(templateDetails)
          this.enabledSwitch = !templateDetails.disabled
          this.templateDialog = true
          this.openXMLEditor(this.template.content)
        })
        .catch(error => {
          // TODO: Do some error handling ?
          return Promise.reject(error)
        })
        .finally(() => {
          this.loadingTemplateDetails = false
        })
    },

    /**
     * openXMLEditor - Open XML Editor
     * @param {string} xmlString
     */
    openXMLEditor(xmlString) {
      let editor
      editor = ace.edit(this.$refs.editorEditable)
      editor.setTheme("ace/theme/eclipse")
      editor.getSession().setMode("ace/mode/velocity")
      editor.session.setValue(xmlString)
    },

    /**
     * Retrieve selected Template details by templateObjet ID
     * @param {string} templateId
     * @returns {Promise}
     */
    getSelectedTemplateDetails(templateId) {
      let templatesQueryObject = {
        byId: templateId
      }
      let template = {}
      return providersDataService
        .retrieveTemplates(
          templatesQueryObject,
          this.VOD_SYCN_TEMPLATE_DATA_SERVICE_URL
        )
        .then(response => {
          if (!this.$lodash.isUndefined(response.data)) {
            template = response.data
          }
          return template
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },

    /**
     * validate - validate template details
     */
    validate() {
      this.$validator.validateAll().then(result => {
        if (result) {
          let editor = ace.edit(this.$refs.editorEditable)
          let xmlString = editor.getValue()
          // Template text Validation
          if (!this.$lodash.isEmpty(xmlString)) {
            this.errorAlert = false
            if (this.isEditMode) {
              this.updateTemplate(xmlString)
            } else {
              this.createTemplate(xmlString)
            }
          } else {
            this.activeTab = 1
            this.errorAlert = true
            this.templateTextError = messages.error.NO_TEMPLATE_TEXT
          }
        } else {
          // Redirect user to form details tab
          if (this.activeTab !== 0) {
            this.activeTab = 0
          }
        }
      })
    },

    /**
     * createTemplate - Create new template
     * @param {string} xmlString
     * @returns {Promise}
     */
    createTemplate(xmlString) {
      this.creatingTemplate = true
      this.template.providerGroup = this.selectedProviderGroup.title
      this.template.content = xmlString
      this.template.disabled = !this.enabledSwitch
      let requestQuery = {
        ownerId: this.selectedProviderGroup.ownerId
      }
      return providersDataService
        .createTemplate(
          this.template,
          requestQuery,
          this.VOD_SYCN_TEMPLATE_DATA_SERVICE_URL
        )
        .then(response => {
          this.templateSuccess = true
          this.failureAlert = false
          this.templateResponseMsg = this.getCreateTemplateSuccessMsg()
          return response
        })
        .catch(error => {
          let response = error.response.data
          this.templateSuccess = false
          this.templateResponseMsg = this.getCreateTemplateFailureMsg(response)
          this.failureAlert = true
          return Promise.reject(error)
        })
        .finally(() => {
          this.createdOrUpdated = true
          // TODO: Collapse in to one boolean instead of two different booleans for create / update
          this.creatingTemplate = false
        })
    },

    /**
     * updateTemplate - update template details
     * @param {string} xmlString
     * @returns {Promise}
     */
    updateTemplate(xmlString) {
      this.editingTemplate = true
      this.template.content = xmlString
      this.template.disabled = !this.enabledSwitch
      return providersDataService
        .updateTemplate(this.template, this.VOD_SYCN_TEMPLATE_DATA_SERVICE_URL)
        .then(response => {
          this.templateSuccess = true
          this.failureAlert = false
          this.templateResponseMsg = this.getEditTemplateSuccessMsg()
          return response
        })
        .catch(error => {
          let response = error.response.data
          this.templateSuccess = false
          this.templateResponseMsg = this.getEditTemplateFailureMsg(response)
          this.failureAlert = true
          return Promise.reject(error)
        })
        .finally(() => {
          this.createdOrUpdated = true
          this.editingTemplate = false
        })
    },

    /**
     * Construct Create Template success message
     */
    getCreateTemplateSuccessMsg() {
      let successMsg = messages.info.CREATE_TEMPLATE_SUCCESS.replace(
        "providerGroup",
        this.selectedProviderGroup.title
      )
      return successMsg
    },

    /**
     * Construct Create Template failure message
     */
    getCreateTemplateFailureMsg(response) {
      let description = response.description
      let failureMsg = messages.error.CREATE_TEMPLATE_FAILURE
      failureMsg += "<br/>" + messages.label.REASON + description
      return failureMsg
    },

    /**
     * Construct Edit Template success message
     */
    getEditTemplateSuccessMsg() {
      let successMsg = messages.info.EDIT_TEMPLATE_SUCCESS
      return successMsg
    },

    /**
     * Construct Edit Template failure message
     */
    getEditTemplateFailureMsg(response) {
      let description = response.description
      let failureMsg = messages.error.EDIT_TEMPLATE_FAILURE
      failureMsg += "<br/>" + messages.label.REASON + description
      return failureMsg
    },

    /**
     * Validator for template title
     */
    templateTitleValidator() {
      this.validatingTitle = true
      return this.isTemplateTitleExists().then(isInUse => {
        this.validatingTitle = false
        if (isInUse) {
          return Promise.resolve({
            valid: false,
            data: {
              message:
                "Template with this title already exists, Choose a different title"
            }
          })
        } else {
          return Promise.resolve({
            valid: true
          })
        }
      })
    },

    /**
     * isTemplateTitleExists - Check if template title already exists
     */
    isTemplateTitleExists() {
      let templateTitleQuery = {
        byProviderGroup: this.selectedProviderGroup.title,
        byTitle: this.template.title
      }
      return providersDataService
        .isTemplateTitleExists(
          templateTitleQuery,
          this.VOD_SYCN_TEMPLATE_DATA_SERVICE_URL
        )
        .then(response => {
          let templates = response.data
          if (
            !this.$lodash.isEmpty(templates) &&
            !this.$lodash.isUndefined(templates)
          ) {
            return Promise.resolve(true)
          } else {
            return Promise.resolve(false)
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },

    /**
     * Validation messages for vee-validate
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          providerGroup: {
            required: () => "Please select a Provider Group"
          },
          description: {
            max: "Description may not be greater than 100 characters"
          },
          subDirectory: {
            max: "Sub Directory may not be greater than 60 characters",
            regex:
              "Sub Directory may only contain alpha-numeric characters, dashes, underscores and spaces"
          },
          templateGroupId: {
            max: "Template Group Id may not be greater than 50 characters"
          }
        }
      }
    },

    /**
     * Re-initialize
     */
    resetTemplateData() {
      this.template = {}
      this.enabledSwitch = true
      this.selectedProviderGroup = ""
      this.creatingTemplate = false
      this.editingTemplate = false
      this.templateTextError = ""
      this.errorAlert = false
      this.failureAlert = false
      this.templateSuccess = false
      this.templateResponseMsg = ""
      this.loadingTemplateDetails = false
      this.activeTab = 0
      this.createdOrUpdated = false
    },

    /**
     * retrieveProviderGroups - Retrieve provider groups and update store
     */
    retrieveProviderGroups() {
      // Update store with provider groups
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingProviderGroups = false
        })
    }
  },
  mounted() {
    // Initialize validator
    this.$validator.localize("en", this.dictionary)
    // custom template title validator
    Validator.extend("templateTitle", {
      validate: this.templateTitleValidator,
      getMessage: (field, params, data) => data.message
    })
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    this.retrieveProviderGroups()
    /**
     * Events
     */
    ProvidersService.$on("event:showManageTemplateModal", this.show)
  },
  beforeDestroy() {
    ProvidersService.$off("event:showManageTemplateModal", this.show)
  }
}
</script>

<style lang="scss">
.style_color {
  color: #757575;
}

.add_margin {
  margin-top: 10px;
}

.vop--ace-editor {
  position: absolute;
  top: 50px;
  bottom: 20px;
  left: 20px;
  right: 20px;
}

.vop--ace-editor .ace_search_field,
.ace_searchbtn:last-child {
  color: black !important;
}

.template-details {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>



// WEBPACK FOOTER //
// src/Providers/ProviderGroups/ManageTemplate.vue